import React, { useState } from 'react';
import { Popover } from '@mui/material';
import { HexColorPicker } from 'react-colorful';
import FormatColorTextIcon from '@mui/icons-material/FormatColorText';
import './ColorPickerPopover.scss';

// Preset colors for quick selection
const PRESET_COLORS = [
    '#000000', '#434343', '#666666', '#999999', '#CCCCCC', '#FFFFFF',
    '#FF0000', '#FF9900', '#FFFF00', '#00FF00', '#00FFFF', '#0000FF',
    '#9900FF', '#FF00FF', '#FF6666', '#FFB366', '#FFFF66', '#66FF66'
];

/**
 * Reusable color picker popover component with preset colors and custom picker
 * @param {string} color - Current color value (hex format or 'transparent')
 * @param {function} onChange - Callback when color changes
 * @param {React.ReactNode} [icon] - Custom icon for the button (defaults to FormatColorTextIcon)
 * @param {string} [title] - Tooltip title for the button
 * @param {string} [className] - Additional CSS class for the wrapper
 * @param {boolean} [showTransparent] - Whether to show transparent color option (for background colors)
 */
export const ColorPickerPopover = ({
    color = '#000000',
    onChange,
    icon,
    title = 'Color',
    className = '',
    showTransparent = false
}) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (e) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleColorChange = (newColor) => {
        onChange(newColor);
    };

    const handlePresetClick = (presetColor) => {
        onChange(presetColor);
    };

    const handleHexInputChange = (e) => {
        const value = e.target.value;
        // Allow typing by updating on valid hex
        if (/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value)) {
            onChange(value);
        }
    };

    return (
        <div className={`color-picker-wrapper ${className}`}>
            <div
                className="color-picker-trigger"
                title={title}
                onClick={handleClick}
            >
                {icon || <FormatColorTextIcon fontSize="small" />}
            </div>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                slotProps={{
                    paper: {
                        className: 'color-picker-popover'
                    }
                }}
            >
                <div className="color-picker-container">
                    {/* Preset colors */}
                    <div className="color-presets">
                        {showTransparent && (
                            <div
                                className={`color-preset transparent ${color === 'transparent' ? 'active' : ''}`}
                                onClick={() => handlePresetClick('transparent')}
                                title="Transparent"
                            />
                        )}
                        {PRESET_COLORS.map((presetColor) => (
                            <div
                                key={presetColor}
                                className={`color-preset ${color?.toUpperCase() === presetColor ? 'active' : ''}`}
                                style={{ backgroundColor: presetColor }}
                                onClick={() => handlePresetClick(presetColor)}
                                title={presetColor}
                            />
                        ))}
                    </div>
                    <HexColorPicker
                        color={color}
                        onChange={handleColorChange}
                    />
                    <div className="color-picker-footer">
                        <input
                            type="text"
                            className="color-hex-input"
                            value={color}
                            onChange={handleHexInputChange}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>
            </Popover>
        </div>
    );
};
